"use client";

import { c as _c } from "react/compiler-runtime";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiLightDrop } from "@/generated/models/ApiLightDrop";
import { Drop } from "@/helpers/waves/drop.helpers";
import useCapacitor from "@/hooks/useCapacitor";
import { useWebsocketStatus } from "@/services/websocket/useWebSocketMessage";
import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { WaveMessages } from "./hooks/types";
import { useActiveWaveManager } from "./hooks/useActiveWaveManager";
import useEnhancedDmWavesList from "./hooks/useEnhancedDmWavesList";
import useEnhancedWavesList, { MinimalWave } from "./hooks/useEnhancedWavesList";
import { useWaveDataManager } from "./hooks/useWaveDataManager";
import useWaveMessagesStore, { Listener as WaveMessagesListener } from "./hooks/useWaveMessagesStore";
import { NextPageProps } from "./hooks/useWavePagination";
import { ProcessIncomingDropType, useWaveRealtimeUpdater } from "./hooks/useWaveRealtimeUpdater";

// Define nested structures for context data
interface WavesContextData {
  readonly list: MinimalWave[];
  readonly isFetching: boolean;
  readonly isFetchingNextPage: boolean;
  readonly hasNextPage: boolean;
  readonly fetchNextPage: () => void;
  readonly addPinnedWave: (id: string) => void;
  readonly removePinnedWave: (id: string) => void;
}
interface ActiveWaveContextData {
  readonly id: string | null;
  readonly set: (waveId: string | null) => void;
}

// Define the interface for the wave messages store functions
interface WaveMessagesStoreData {
  readonly getData: (key: string) => WaveMessages | undefined;
  readonly subscribe: (key: string, listener: WaveMessagesListener) => void;
  readonly unsubscribe: (key: string, listener: WaveMessagesListener) => void;
}

// Define the type for our context using nested structures
interface MyStreamContextType {
  readonly waves: WavesContextData;
  readonly directMessages: WavesContextData;
  readonly activeWave: ActiveWaveContextData;
  readonly waveMessagesStore: WaveMessagesStoreData;
  readonly registerWave: (waveId: string, syncNewest?: boolean) => void;
  readonly fetchNextPageForWave: (props: NextPageProps) => Promise<(ApiDrop | ApiLightDrop)[] | null>;
  readonly fetchAroundSerialNo: (waveId: string, serialNo: number) => void;
  readonly processIncomingDrop: (drop: ApiDrop, type: ProcessIncomingDropType) => void;
  readonly processDropRemoved: (waveId: string, dropId: string) => void;
  readonly applyOptimisticDropUpdate: ({
    waveId,
    dropId,
    update
  }: {
    waveId: string;
    dropId: string;
    update: (draft: Drop) => Drop | void;
  }) => {
    rollback: () => void;
  } | null;
}
interface MyStreamProviderProps {
  readonly children: ReactNode;
}

// Create the context
const MyStreamContext = createContext<MyStreamContextType | null>(null);

// Create a provider component
export const MyStreamProvider: React.FC<MyStreamProviderProps> = t0 => {
  const $ = _c(71);
  const {
    children
  } = t0;
  const {
    isCapacitor,
    isActive
  } = useCapacitor();
  const {
    activeWaveId,
    setActiveWave
  } = useActiveWaveManager();
  const wavesHookData = useEnhancedWavesList(activeWaveId);
  const dmWavesHookData = useEnhancedDmWavesList(activeWaveId);
  const waveMessagesStore = useWaveMessagesStore();
  const websocketStatus = useWebsocketStatus();
  const prevIsActiveRef = useRef(isActive);
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  let t1;
  if ($[0] !== waveMessagesStore.getData || $[1] !== waveMessagesStore.removeDrop || $[2] !== waveMessagesStore.updateData) {
    t1 = {
      updateData: waveMessagesStore.updateData,
      getData: waveMessagesStore.getData,
      removeDrop: waveMessagesStore.removeDrop
    };
    $[0] = waveMessagesStore.getData;
    $[1] = waveMessagesStore.removeDrop;
    $[2] = waveMessagesStore.updateData;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const waveDataManager = useWaveDataManager(t1);
  let t2;
  if ($[4] !== activeWaveId || $[5] !== removeWaveDeliveredNotifications || $[6] !== waveDataManager.registerWave || $[7] !== waveDataManager.syncNewestMessages || $[8] !== waveMessagesStore.getData || $[9] !== waveMessagesStore.removeDrop || $[10] !== waveMessagesStore.updateData) {
    t2 = {
      activeWaveId,
      getData: waveMessagesStore.getData,
      updateData: waveMessagesStore.updateData,
      registerWave: waveDataManager.registerWave,
      syncNewestMessages: waveDataManager.syncNewestMessages,
      removeDrop: waveMessagesStore.removeDrop,
      removeWaveDeliveredNotifications
    };
    $[4] = activeWaveId;
    $[5] = removeWaveDeliveredNotifications;
    $[6] = waveDataManager.registerWave;
    $[7] = waveDataManager.syncNewestMessages;
    $[8] = waveMessagesStore.getData;
    $[9] = waveMessagesStore.removeDrop;
    $[10] = waveMessagesStore.updateData;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const {
    processIncomingDrop,
    processDropRemoved
  } = useWaveRealtimeUpdater(t2);
  let t3;
  if ($[12] !== activeWaveId || $[13] !== isCapacitor || $[14] !== waveDataManager || $[15] !== wavesHookData || $[16] !== websocketStatus) {
    t3 = () => {
      if (websocketStatus !== "connected") {
        return;
      }
      if (activeWaveId) {
        waveDataManager.registerWave(activeWaveId, true);
      }
      wavesHookData.refetchAllWaves();
      if (isCapacitor) {
        wavesHookData.resetAllWavesNewDropsCount();
      }
    };
    $[12] = activeWaveId;
    $[13] = isCapacitor;
    $[14] = waveDataManager;
    $[15] = wavesHookData;
    $[16] = websocketStatus;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== activeWaveId || $[19] !== isCapacitor || $[20] !== websocketStatus) {
    t4 = [websocketStatus, activeWaveId, isCapacitor];
    $[18] = activeWaveId;
    $[19] = isCapacitor;
    $[20] = websocketStatus;
    $[21] = t4;
  } else {
    t4 = $[21];
  }
  useEffect(t3, t4);
  let t5;
  if ($[22] !== activeWaveId || $[23] !== waveDataManager) {
    t5 = () => {
      if (activeWaveId) {
        waveDataManager.registerWave(activeWaveId, true);
      }
    };
    $[22] = activeWaveId;
    $[23] = waveDataManager;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  let t6;
  if ($[25] !== activeWaveId) {
    t6 = [activeWaveId];
    $[25] = activeWaveId;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[27] !== activeWaveId || $[28] !== isActive || $[29] !== isCapacitor || $[30] !== waveDataManager || $[31] !== wavesHookData) {
    t7 = () => {
      if (!isCapacitor) {
        return;
      }
      if (!prevIsActiveRef.current && isActive) {
        if (activeWaveId) {
          waveDataManager.registerWave(activeWaveId, true);
        }
        wavesHookData.refetchAllWaves();
        wavesHookData.resetAllWavesNewDropsCount();
      }
      prevIsActiveRef.current = isActive;
    };
    t8 = [isActive, isCapacitor, activeWaveId, waveDataManager, wavesHookData];
    $[27] = activeWaveId;
    $[28] = isActive;
    $[29] = isCapacitor;
    $[30] = waveDataManager;
    $[31] = wavesHookData;
    $[32] = t7;
    $[33] = t8;
  } else {
    t7 = $[32];
    t8 = $[33];
  }
  useEffect(t7, t8);
  let t9;
  if ($[34] !== wavesHookData.addPinnedWave || $[35] !== wavesHookData.fetchNextPage || $[36] !== wavesHookData.hasNextPage || $[37] !== wavesHookData.isFetching || $[38] !== wavesHookData.isFetchingNextPage || $[39] !== wavesHookData.removePinnedWave || $[40] !== wavesHookData.waves) {
    t9 = {
      list: wavesHookData.waves,
      isFetching: wavesHookData.isFetching,
      isFetchingNextPage: wavesHookData.isFetchingNextPage,
      hasNextPage: wavesHookData.hasNextPage,
      fetchNextPage: wavesHookData.fetchNextPage,
      addPinnedWave: wavesHookData.addPinnedWave,
      removePinnedWave: wavesHookData.removePinnedWave
    };
    $[34] = wavesHookData.addPinnedWave;
    $[35] = wavesHookData.fetchNextPage;
    $[36] = wavesHookData.hasNextPage;
    $[37] = wavesHookData.isFetching;
    $[38] = wavesHookData.isFetchingNextPage;
    $[39] = wavesHookData.removePinnedWave;
    $[40] = wavesHookData.waves;
    $[41] = t9;
  } else {
    t9 = $[41];
  }
  const waves = t9;
  let t10;
  if ($[42] !== dmWavesHookData.addPinnedWave || $[43] !== dmWavesHookData.fetchNextPage || $[44] !== dmWavesHookData.hasNextPage || $[45] !== dmWavesHookData.isFetching || $[46] !== dmWavesHookData.isFetchingNextPage || $[47] !== dmWavesHookData.removePinnedWave || $[48] !== dmWavesHookData.waves) {
    t10 = {
      list: dmWavesHookData.waves,
      isFetching: dmWavesHookData.isFetching,
      isFetchingNextPage: dmWavesHookData.isFetchingNextPage,
      hasNextPage: dmWavesHookData.hasNextPage,
      fetchNextPage: dmWavesHookData.fetchNextPage,
      addPinnedWave: dmWavesHookData.addPinnedWave,
      removePinnedWave: dmWavesHookData.removePinnedWave
    };
    $[42] = dmWavesHookData.addPinnedWave;
    $[43] = dmWavesHookData.fetchNextPage;
    $[44] = dmWavesHookData.hasNextPage;
    $[45] = dmWavesHookData.isFetching;
    $[46] = dmWavesHookData.isFetchingNextPage;
    $[47] = dmWavesHookData.removePinnedWave;
    $[48] = dmWavesHookData.waves;
    $[49] = t10;
  } else {
    t10 = $[49];
  }
  const directMessages = t10;
  let t11;
  if ($[50] !== activeWaveId || $[51] !== setActiveWave) {
    t11 = {
      id: activeWaveId,
      set: setActiveWave
    };
    $[50] = activeWaveId;
    $[51] = setActiveWave;
    $[52] = t11;
  } else {
    t11 = $[52];
  }
  const activeWave = t11;
  let t12;
  if ($[53] !== waveMessagesStore.getData || $[54] !== waveMessagesStore.subscribe || $[55] !== waveMessagesStore.unsubscribe) {
    t12 = {
      getData: waveMessagesStore.getData,
      subscribe: waveMessagesStore.subscribe,
      unsubscribe: waveMessagesStore.unsubscribe
    };
    $[53] = waveMessagesStore.getData;
    $[54] = waveMessagesStore.subscribe;
    $[55] = waveMessagesStore.unsubscribe;
    $[56] = t12;
  } else {
    t12 = $[56];
  }
  const waveMessagesStoreData = t12;
  let t13;
  if ($[57] !== activeWave || $[58] !== directMessages || $[59] !== processDropRemoved || $[60] !== processIncomingDrop || $[61] !== waveDataManager.fetchAroundSerialNo || $[62] !== waveDataManager.fetchNextPage || $[63] !== waveDataManager.registerWave || $[64] !== waveMessagesStore.optimisticUpdateDrop || $[65] !== waveMessagesStoreData || $[66] !== waves) {
    t13 = {
      waves,
      directMessages,
      activeWave,
      waveMessagesStore: waveMessagesStoreData,
      registerWave: waveDataManager.registerWave,
      fetchNextPageForWave: waveDataManager.fetchNextPage,
      fetchAroundSerialNo: waveDataManager.fetchAroundSerialNo,
      processIncomingDrop,
      processDropRemoved,
      applyOptimisticDropUpdate: waveMessagesStore.optimisticUpdateDrop
    };
    $[57] = activeWave;
    $[58] = directMessages;
    $[59] = processDropRemoved;
    $[60] = processIncomingDrop;
    $[61] = waveDataManager.fetchAroundSerialNo;
    $[62] = waveDataManager.fetchNextPage;
    $[63] = waveDataManager.registerWave;
    $[64] = waveMessagesStore.optimisticUpdateDrop;
    $[65] = waveMessagesStoreData;
    $[66] = waves;
    $[67] = t13;
  } else {
    t13 = $[67];
  }
  const contextValue = t13;
  let t14;
  if ($[68] !== children || $[69] !== contextValue) {
    t14 = <MyStreamContext.Provider value={contextValue}>{children}</MyStreamContext.Provider>;
    $[68] = children;
    $[69] = contextValue;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  return t14;
};

// Create a custom hook for using this context
export const useMyStream = () => {
  const context = useContext(MyStreamContext);
  if (!context) {
    throw new Error("useMyStream must be used within a MyStreamProvider");
  }
  return context;
};
export const useMyStreamOptional = () => {
  return useContext(MyStreamContext);
};

// Create the selector hook for wave messages
export function useMyStreamWaveMessages(waveId) {
  const $ = _c(12);
  const {
    waveMessagesStore
  } = useMyStream();
  const {
    getData,
    subscribe,
    unsubscribe
  } = waveMessagesStore;
  let t0;
  if ($[0] !== getData || $[1] !== waveId) {
    t0 = () => waveId ? getData(waveId) : undefined;
    $[0] = getData;
    $[1] = waveId;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [data, setData] = useState(t0);
  let t1;
  let t2;
  if ($[3] !== subscribe || $[4] !== unsubscribe || $[5] !== waveId) {
    t1 = () => {
      if (!waveId) {
        setData(undefined);
        return;
      }
      const listener = newData => {
        setData(currentData => {
          if (JSON.stringify(currentData) !== JSON.stringify(newData)) {
            return newData;
          }
          return currentData;
        });
      };
      subscribe(waveId, listener);
      return () => {
        unsubscribe(waveId, listener);
      };
    };
    t2 = [waveId, subscribe, unsubscribe];
    $[3] = subscribe;
    $[4] = unsubscribe;
    $[5] = waveId;
    $[6] = t1;
    $[7] = t2;
  } else {
    t1 = $[6];
    t2 = $[7];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[8] !== getData || $[9] !== waveId) {
    t3 = () => {
      setData(waveId ? getData(waveId) : undefined);
    };
    t4 = [waveId, getData];
    $[8] = getData;
    $[9] = waveId;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  useEffect(t3, t4);
  return data;
}